/* Treatments Hub: tab navigation + live search.

   This file is loaded (deferred) from every treatment-hub-*.liquid section,
   since each is an independent, reorderable/removable Shopify section and
   any of them could be the first one present on the page. Because a classic
   (non-module) <script> tag re-executes its top-level code every time it
   appears in the DOM, each custom element definition is guarded with the
   same `customElements.get(...)` idempotency check already used elsewhere
   in this theme (product-form.js, quick-add.js, etc.) so repeated loads are
   harmless no-ops after the first. */

if (!customElements.get('treatment-hub-tabs')) {
  class TreatmentHubTabs extends HTMLElement {
    connectedCallback() {
      this.panels = Array.from(document.querySelectorAll('[data-hub-panel]'));
      if (!this.panels.length) return;

      // Group panels by data-hub-key: a merchant may split one oversized
      // category across two section instances that share the same key.
      this.tabs = new Map();
      this.panels.forEach((panel) => {
        const key = panel.dataset.hubKey;
        if (!this.tabs.has(key)) {
          this.tabs.set(key, { label: panel.dataset.hubLabel || key, count: 0, panels: [] });
        }
        const tab = this.tabs.get(key);
        tab.count += Number(panel.dataset.hubCount || 0);
        tab.panels.push(panel);
      });

      this.setAttribute('role', 'tablist');
      if (!this.hasAttribute('aria-label')) {
        this.setAttribute('aria-label', 'Treatment categories');
      }

      this.buttons = [];
      this.tabs.forEach((tab, key) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'treatment-hub-tabs__tab';
        button.setAttribute('role', 'tab');
        button.id = `hub-tab-${key}`;
        button.setAttribute('aria-controls', tab.panels.map((panel) => panel.id).join(' '));
        button.dataset.hubTabTarget = key;
        button.innerHTML = `${tab.label}<span class="treatment-hub-tabs__count">${tab.count}</span>`;
        button.addEventListener('click', () => this.activate(key, true));
        button.addEventListener('keydown', (event) => this.onKeydown(event));
        this.appendChild(button);
        this.buttons.push(button);
      });

      this.searchActive = false;

      this.updateStickyOffset();
      window.addEventListener('resize', () => this.updateStickyOffset());
      window.addEventListener('load', () => this.updateStickyOffset());

      const initialKey = this.keyFromHash() || this.buttons[0].dataset.hubTabTarget;
      this.activate(initialKey, false);

      window.addEventListener('hashchange', () => {
        const key = this.keyFromHash();
        if (key) this.activate(key, false);
      });

      document.addEventListener('hub:search-active', () => this.onSearchActive());
      document.addEventListener('hub:search-clear', () => this.onSearchClear());
    }

    keyFromHash() {
      const key = window.location.hash.replace('#', '');
      return key && this.tabs.has(key) ? key : null;
    }

    // The tab bar's own section is sticky (see treatment-hub-tabs.liquid);
    // when it is, a scrolled-to panel needs its heading to clear the bar's
    // real rendered height as well as the theme's sticky header. Also used
    // as the `scroll-margin-top` fallback for any plain "#key" anchor link
    // elsewhere in the theme that isn't driven by this component's own JS.
    updateStickyOffset() {
      const section = this.closest('[id^="shopify-section-"]');
      const isSticky = section && getComputedStyle(section).position === 'sticky';
      document.documentElement.style.setProperty('--hub-tabs-height', isSticky ? `${section.offsetHeight}px` : '0px');
    }

    // How much of the very top of the viewport is covered by fixed/sticky
    // chrome (site header + this sticky tab bar) once scrolling has
    // stopped, in px.
    getScrollOffset() {
      const rootStyle = getComputedStyle(document.documentElement);
      const headerHeight = parseFloat(rootStyle.getPropertyValue('--header-height')) || 0;
      const tabsHeight = parseFloat(rootStyle.getPropertyValue('--hub-tabs-height')) || 0;
      return headerHeight + tabsHeight;
    }

    // Scrolls so `target`'s top edge clears the sticky header + tab bar.
    // Deliberately not `target.scrollIntoView({ behavior: 'smooth' })`:
    // browsers compute that animation's endpoint once, before the tab
    // bar has settled into its stuck position, so the page visibly
    // overshoots (jumps toward the bottom) and then corrects itself back
    // up. Measuring the target's position directly with
    // getBoundingClientRect() and animating with window.scrollTo() avoids
    // that browser-side re-evaluation entirely.
    scrollToPanel(target) {
      this.updateStickyOffset();
      requestAnimationFrame(() => {
        const top = target.getBoundingClientRect().top + window.scrollY - this.getScrollOffset();
        window.scrollTo({ top, behavior: 'smooth' });
      });
    }

    onKeydown(event) {
      const currentIndex = this.buttons.indexOf(event.currentTarget);
      let nextIndex = null;
      if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.buttons.length;
      if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.buttons.length) % this.buttons.length;
      if (event.key === 'Home') nextIndex = 0;
      if (event.key === 'End') nextIndex = this.buttons.length - 1;
      if (nextIndex === null) return;
      event.preventDefault();
      const target = this.buttons[nextIndex];
      target.focus();
      this.activate(target.dataset.hubTabTarget, true);
    }

    activate(key, updateHash) {
      this.activeKey = key;
      this.buttons.forEach((button) => {
        const selected = button.dataset.hubTabTarget === key;
        button.setAttribute('aria-selected', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      if (!this.searchActive) {
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.hubKey !== key;
        });
      }
      if (updateHash) window.history.replaceState(null, '', `#${key}`);
      // Only for a genuine tab switch (click/keyboard), not the initial
      // activate() on load or the reset after clearing a search, so the
      // page doesn't jump on its own.
      if (updateHash) {
        const target = this.tabs.get(key).panels[0];
        if (target) this.scrollToPanel(target);
      }
    }

    onSearchActive() {
      this.searchActive = true;
      this.panels.forEach((panel) => {
        panel.hidden = false;
      });
      const wrapper = this.closest('.treatment-hub-tabs-wrapper');
      if (wrapper) wrapper.hidden = true;
    }

    onSearchClear() {
      this.searchActive = false;
      const wrapper = this.closest('.treatment-hub-tabs-wrapper');
      if (wrapper) wrapper.hidden = false;
      this.activate(this.activeKey || this.buttons[0].dataset.hubTabTarget, false);
    }
  }

  customElements.define('treatment-hub-tabs', TreatmentHubTabs);
}

if (!customElements.get('treatment-hub-search')) {
  class TreatmentHubSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-hub-search-input]');
      this.status = this.querySelector('[data-hub-search-status]');
      this.emptyState = this.querySelector('[data-hub-search-empty]');
      this.clearButton = this.querySelector('[data-hub-search-clear]');
      if (!this.input) return;

      const handleInput = typeof window.debounce === 'function'
        ? window.debounce(() => this.onInput(), 180)
        : () => this.onInput();

      this.input.addEventListener('input', handleInput);

      if (this.clearButton) {
        this.clearButton.addEventListener('click', () => {
          this.input.value = '';
          this.onInput();
          this.input.focus();
        });
      }
    }

    onInput() {
      const query = this.input.value.trim().toLowerCase();
      const items = document.querySelectorAll('[data-hub-item]');

      if (!query) {
        items.forEach((item) => {
          item.hidden = false;
        });
        // Panel visibility is restored by TreatmentHubTabs.onSearchClear()
        // (via activate(), based on the active tab), but groups and
        // two-column layout columns aren't tab-aware, so they need
        // resetting here or they'd stay hidden.
        document.querySelectorAll('[data-hub-group]').forEach((group) => {
          group.hidden = false;
        });
        document.querySelectorAll('.treatment-hub-price-list__col').forEach((col) => {
          col.hidden = false;
        });
        if (this.status) this.status.textContent = '';
        if (this.emptyState) this.emptyState.hidden = true;
        document.dispatchEvent(new CustomEvent('hub:search-clear'));
        return;
      }

      // A group name (e.g. "Threading", "Waxing") matching any one word of
      // the query pulls in every item under it, even if an individual
      // item's own name doesn't contain that word (e.g. "Full leg &
      // Hollywood" wouldn't otherwise match a search for "standard bikini
      // waxing" — the group name only has to match "waxing", not the
      // whole, multi-word phrase).
      const words = query.split(/\s+/).filter(Boolean);
      const matchingGroups = new Set();
      document.querySelectorAll('[data-hub-group]').forEach((group) => {
        const groupName = (group.dataset.hubGroupName || '').toLowerCase();
        if (groupName && words.some((word) => groupName.indexOf(word) > -1)) {
          matchingGroups.add(group);
        }
      });

      let matchCount = 0;
      items.forEach((item) => {
        const haystack = `${item.dataset.hubName || ''} ${item.dataset.hubNote || ''} ${item.dataset.hubCategory || ''}`.toLowerCase();
        const group = item.closest('[data-hub-group]');
        const isMatch = haystack.indexOf(query) > -1 || (group && matchingGroups.has(group));
        item.hidden = !isMatch;
        if (isMatch) matchCount += 1;
      });

      if (this.status) {
        this.status.textContent = this.buildStatusMessage(matchCount, this.input.value.trim());
      }
      if (this.emptyState) this.emptyState.hidden = matchCount !== 0;

      // Dispatched before the panel-level hide below: its listener
      // (TreatmentHubTabs.onSearchActive) unhides every panel so items can
      // be matched across all categories, which would otherwise undo it.
      document.dispatchEvent(new CustomEvent('hub:search-active', { detail: { query, matchCount } }));

      // Hide each whole panel (heading, description and all) once none of
      // its items match, rather than leaving an empty section on screen.
      // Groups (e.g. "Threading", "Brows and lashes" within a price-list
      // panel) get the same treatment so a panel that still has matches
      // elsewhere doesn't show other empty, labelled boxes alongside them.
      // In a two-column panel (e.g. Beauty essentials), an empty column
      // left in the grid pushes the other, non-empty column into the
      // right-hand track — hiding it lets the remaining column fall back
      // into the grid's first (left) track instead.
      document.querySelectorAll('[data-hub-group]').forEach((group) => {
        group.hidden = !group.querySelector('[data-hub-item]:not([hidden])');
      });
      document.querySelectorAll('.treatment-hub-price-list__col').forEach((col) => {
        col.hidden = !col.querySelector('[data-hub-item]:not([hidden])');
      });
      document.querySelectorAll('[data-hub-panel]').forEach((panel) => {
        panel.hidden = !panel.querySelector('[data-hub-item]:not([hidden])');
      });
    }

    buildStatusMessage(matchCount, term) {
      if (matchCount === 0) {
        return this.dataset.emptyState || '';
      }
      const template = matchCount === 1 ? this.dataset.countTemplateOne : this.dataset.countTemplateOther;
      if (!template) return '';
      // split/join instead of a second .replace(): a search term containing
      // "$&" or "$1" would otherwise be misread as a replacement pattern.
      return template.replace(/\d+/, String(matchCount)).split('__QUERY__').join(term);
    }
  }

  customElements.define('treatment-hub-search', TreatmentHubSearch);
}
